import { getNestedProperty } from '@opallabs/lambda'
import { assetProxyImageUrl, assetProxyPreviewUrl } from '@opallabs/routing'

const spacingParams = {
  CELL_PADDING: 20, // the total space betwen moments (same horizontal and vertical)
  MOMENT_VERTICAL_IMAGE_MARGIN: 74, // the difference in height between a moment card and its preview image
  MOMENT_BORDER_WIDTH: 4, // total width of the border on a moment card
  COVERIMAGE_WIDTH: 250, // the width the frontend should resize a fullsize asset
  WEEKVIEW_OUTERPADDING: 38, // the difference in width of the weekview component and the 7 columns of momentcards
  MOMENT_HEIGHT: 89, // the default height of a moment
}

// Calculate the "optimal" coverImage width, given the current width of the weekview component
export const calcCoverImageWidth = (width) =>
  (width -
    spacingParams.WEEKVIEW_OUTERPADDING -
    7 * (spacingParams.CELL_PADDING + spacingParams.MOMENT_BORDER_WIDTH)) /
  7

// Calculate moment height from image dimensions and the "optimal" image width
export const calcMomentHeight = (preview, coverImageWidth) => {
  if (!preview) return spacingParams.MOMENT_HEIGHT
  return (
    (coverImageWidth * preview.height) / preview.width +
    spacingParams.MOMENT_VERTICAL_IMAGE_MARGIN
  )
}

export const deDupe = (arr, nestedParam) =>
  arr.reduce(
    (acc, curr) =>
      acc.map((s) => s && s[nestedParam]).includes(curr && curr[nestedParam])
        ? acc
        : [...acc, curr],
    []
  )

export const getRelevantImageProperties = (moment, assetReference = null) => {
  let asset = assetReference

  // Default to primary asset reference if one is not provided
  if (asset === null) {
    asset = getNestedProperty(moment, 'primary_asset_reference')
    if (asset && asset.cover_asset_reference)
      asset = asset.cover_asset_reference
  }

  if (!asset) return null

  const previewableAssetRef = (assetRef) =>
    assetRef.asset_type !== 'file' || assetRef.format === 'pdf'

  // if our primary asset reference is a non-previewable file,
  // find the next previewable asset
  if (asset.asset_type === 'file' && asset.format !== 'pdf') {
    asset = (moment.asset_references || []).find(previewableAssetRef)
  }

  // If we don't have a non-file asset, return
  if (!asset) return null

  const assetProxyParams = {
    uuid: asset.uuid,
    width: spacingParams.COVERIMAGE_WIDTH,
    height: null,
    queryParams: {
      fit: 'inside',
    },
  }

  return {
    url:
      asset.asset_type === 'video'
        ? assetProxyPreviewUrl(assetProxyParams)
        : assetProxyImageUrl(assetProxyParams),
    height: asset.height,
    width: asset.width,
  }
}

export const parseMomentData = (
  moment,
  content,
  currentStoryId,
  currentBrandId,
  width
) => {
  const relatedContent = getNestedProperty(moment, 'relationships', 'posts')
  const relatedContentIds =
    (relatedContent && relatedContent.map((c) => c.id)) || []
  let contentLabels = []
  let approvalStatuses
  let posts
  let preview

  /* calculate the statuses, services, labels if content has been returned */
  const loadedContentIds = content
    ? relatedContentIds.filter((id) => content[id])
    : []
  if (loadedContentIds.length) {
    /* The `posts` resolved by spreading the proxied moment below come from the
    state snapshot captured when the moment was wrapped, which can predate the
    week's content fetch — leaving them as empty stubs that defeat
    MomentFilter's child-post fallback (e.g. filtering by an account-inherited
    label). Prefer the freshly selected content passed in, even when some of it
    never arrives, such as content the person cannot see. */
    posts = loadedContentIds.map((id) => content[id])
    approvalStatuses = new Set(posts.map((post) => post.status))
    /* get unduplicated Set of all labels from moment and associated content */
    contentLabels = posts.reduce((acc, post) => {
      if (post.labels) {
        return [...acc, ...post.labels]
      }
      return acc
    }, [])
  }

  if (content && relatedContentIds.length) {
    preview = getRelevantImageProperties(moment)
  }

  const momentLabels = moment.labels || []

  const deDupedLabels = deDupe([...momentLabels, ...contentLabels], 'id')
  const labelSets = deDupe(
    deDupedLabels.map((label) => label.label_set),
    'id'
  )

  const storyInView =
    moment.stories && moment.stories.find((s) => s.id === currentStoryId)

  /* if the moment has returned in this indexed set but has a different
  brand than the current brand, it must be a shared moment. For annotation,
  we therefore apply the current brand to the moment. The primary brand is
  passed so that correct window.open click navigation can occur */
  let correctedBrand = moment.brand
  let primaryBrand
  if (
    moment.brand &&
    getNestedProperty(moment, 'brand', 'id') !== currentBrandId.toString()
  ) {
    correctedBrand = {
      id: currentBrandId && currentBrandId.toString(),
      type: 'brand',
    }
    primaryBrand = moment.brand
  }

  const coverImageWidth = calcCoverImageWidth(width)
  const momentHeight = calcMomentHeight(preview, coverImageWidth)

  return {
    ...moment,
    ...(posts && { posts }),
    height: momentHeight || 100,
    brand: correctedBrand,
    primaryBrand,
    labels: deDupedLabels,
    labelSets,
    approvalStatuses,
    preview,
    primaryStory: moment.primary_story,
    isPrivate: !!moment.privacy_group,
    storyInView,
    storyNames: moment.stories && moment.stories.map((s) => s.name),
  }
}
